import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Lock, Check } from 'lucide-react';

const LevelSelect = ({ 
  currentLevel, 
  maxLevel,
  isHardMode,
  onLevelSelect,
  isGuestMode,
  theme 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const scrollContainerRef = useRef(null);
  
  // Create array of level numbers from 1 to maxLevel
  const levels = Array.from({ length: Math.max(500, maxLevel) }, (_, i) => i + 1);
  
  // Scroll to current level whenever dropdown opens
  useEffect(() => {
    if (isOpen && scrollContainerRef.current) {
      const containerElement = scrollContainerRef.current;
      const levelElement = containerElement.querySelector(`[data-level="${currentLevel}"]`);
      
      if (levelElement) {
        setTimeout(() => {
          const containerRect = containerElement.getBoundingClientRect();
          const elementRect = levelElement.getBoundingClientRect();
          containerElement.scrollTop = elementRect.top - containerRect.top - containerRect.height / 2 + elementRect.height / 2;
        }, 50);
      }
    }
  }, [isOpen, currentLevel]);
  
  return (
    <div className="relative w-full flex flex-col items-center mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          group flex items-center gap-1 px-2 py-1 
          ${theme.text} hover:opacity-80 transition-colors
        `}
        aria-label={isOpen ? "Hide Level Select" : "Show Level Select"}
      >
        <span className="text-sm font-medium">
          {isHardMode ? 'Challenge Level ' : 'Level '}
          {currentLevel}
        </span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5" />
        ) : (
          <ChevronDown className="w-5 h-5" />
        )}
      </button>

      {isOpen && (
        <div className={`
          absolute top-full mt-1 w-[600px] ${theme.container}
          rounded-lg shadow-lg z-50 left-1/2 -translate-x-1/2 border ${theme.border}
        `}>
          <div className="p-4">
            <div 
              ref={scrollContainerRef}
              className="max-h-[400px] overflow-y-auto pr-2"
            >
              <div className="grid grid-cols-5 gap-4">
                {levels.map((level) => {
                  const isUnlocked = level <= maxLevel;
                  const isCurrentLevel = level === currentLevel;
                  const isCompleted = isUnlocked && level < maxLevel;
                  
                  return (
                    <button
                      key={level}
                      data-level={level}
                      onClick={() => {
                        if (isUnlocked) {
                          onLevelSelect(level);
                          setIsOpen(false);
                        }
                      }}
                      disabled={!isUnlocked}
                      className={`
                        relative p-4 rounded-lg text-center transition-all
                        ${isUnlocked 
                          ? `hover:${theme.containerHover} cursor-pointer` 
                          : `${theme.containerBg} cursor-not-allowed opacity-50`
                        }
                        ${isCurrentLevel 
                          ? 'bg-blue-100 dark:bg-blue-900 ring-2 ring-blue-500' 
                          : theme.containerBg
                        }
                      `}
                    >
                      <div className={`text-lg font-bold mb-1 ${theme.text}`}>
                        Level {level}
                      </div>
                      <div className="flex justify-center">
                        {!isUnlocked && <Lock className={`w-4 h-4 ${theme.subtext}`} />}
                        {isCompleted && (
                          <Check className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LevelSelect;